import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Checkbox,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  GetApp as ExportIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchEventTypes,
  searchEventTypes,
  deleteEventType,
  bulkActivate,
  bulkDeactivate,
  bulkDelete,
  exportToCSV,
  fetchStatistics,
  setSearchParams,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setPagination,
} from '../../store/reducers/eventTypeSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    textAlign: 'center',
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: 'center',
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    minWidth: 80,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const EventTypeList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    items,
    loading,
    error,
    pagination,
    statistics,
    selectedIds,
    bulkOperationLoading,
    successMessage,
  } = useSelector((state: RootState) => state.eventType);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchEventTypes());
    dispatch(fetchStatistics());
  }, [dispatch]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        // Clear success message
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSearch = () => {
    const params = {
      page: 1,
      pageSize: pagination.pageSize,
      search: searchTerm,
      active: activeFilter === 'all' ? undefined : activeFilter === 'true',
    };
    dispatch(setSearchParams(params));
    dispatch(searchEventTypes(params));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveFilter('all');
    const params = {
      page: 1,
      pageSize: pagination.pageSize,
      search: '',
      active: undefined,
    };
    dispatch(setSearchParams(params));
    dispatch(fetchEventTypes(params));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    const params = {
      page: newPage + 1,
      pageSize: pagination.pageSize,
    };
    dispatch(setPagination(params));
    dispatch(fetchEventTypes(params));
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const params = {
      page: 1,
      pageSize: parseInt(event.target.value, 10),
    };
    dispatch(setPagination(params));
    dispatch(fetchEventTypes(params));
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(setSelectedIds(items.map(item => item.Id!)));
    } else {
      dispatch(clearSelectedIds());
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      dispatch(addSelectedId(id));
    } else {
      dispatch(removeSelectedId(id));
    }
  };

  const handleBulkActivate = () => {
    if (selectedIds.length > 0) {
      dispatch(bulkActivate(selectedIds));
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedIds.length > 0) {
      dispatch(bulkDeactivate(selectedIds));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length > 0 && window.confirm(`Are you sure you want to delete ${selectedIds.length} Event Type(s)?`)) {
      dispatch(bulkDelete(selectedIds));
    }
  };

  const handleExport = () => {
    const params = {
      search: searchTerm,
      active: activeFilter === 'all' ? undefined : activeFilter === 'true',
    };
    dispatch(exportToCSV(params));
  };

  const handleEdit = (id: number) => {
    navigate(`/catalogs/eventtype/edit/${id}`);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this Event Type?')) {
      dispatch(deleteEventType(id));
    }
  };

  if (loading && items.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4">Event Types</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/catalogs/eventtype/add')}
        >
          Add New Event Type
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box className={classes.statsContainer}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {statistics.total}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Event Types
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {statistics.active}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Active Event Types
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {statistics.inactive}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Inactive Event Types
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Search and Filters */}
      <Box className={classes.searchContainer}>
        <TextField
          label="Search Event Types"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          style={{ minWidth: 200 }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as string)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClearSearch}
        >
          Clear
        </Button>
      </Box>

      {/* Bulk Action Buttons */}
      {selectedIds.length > 0 && (
        <Box className={classes.actionButtons}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={handleBulkActivate}
            disabled={bulkOperationLoading}
          >
            Activate ({selectedIds.length})
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CancelIcon />}
            onClick={handleBulkDeactivate}
            disabled={bulkOperationLoading}
          >
            Deactivate ({selectedIds.length})
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            disabled={bulkOperationLoading}
          >
            Delete ({selectedIds.length})
          </Button>
        </Box>
      )}

      {/* Export Button */}
      <Box className={classes.actionButtons}>
        <Button
          variant="outlined"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={loading}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Table */}
      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < items.length}
                    checked={items.length > 0 && selectedIds.length === items.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.Id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(item.Id!)}
                      onChange={(e) => handleSelectItem(item.Id!, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{item.Name}</TableCell>
                  <TableCell>{item.Description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.Active ? 'Active' : 'Inactive'}
                      color={item.Active ? 'primary' : 'default'}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(item.Id!)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleDelete(item.Id!)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.pageSize}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Paper>
    </Box>
  );
};

export default EventTypeList; 